import { useActiveSubscription } from "@/tanstack-hooks";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import Spinner from "../Spinner";
import {
  PaymentGatewayType,
  SubscriptionDetails,
  SubscriptionStatus,
} from "@/generated-api";
import { Button } from "../ui/button";
import { CreditCardIcon } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetAccountLocale } from "@/hooks/useGetAccountLocale";
import { useNavigate } from "react-router-dom";
import { formatSubscriptionDate } from "@/lib/utils";
import CancelSubscriptionButton from "./CancelSubscriptionButton";

function formatCardType(type: string | null) {
  if (!type) return;
  return type
    .split(/[_\s]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
}

export default function MembershipManagement() {
  const activeSubscriptionQuery = useActiveSubscription(true);
  const t = useTranslation();
  const locale = useGetAccountLocale();
  const navigate = useNavigate();

  function FreePreview() {
    return (
      <div className="flex flex-col gap-y-2">
        <h4 className="font-bold">
          {t.MEMBERSHIP_SETTINGS.LABELS.FREE_PREVIEW_TITLE}
        </h4>
        <div className="text-sm text-gray-400">
          {t.MEMBERSHIP_SETTINGS.LABELS.FREE_PREVIEW_DESCRIPTION}
        </div>
      </div>
    );
  }

  function FullMember({ period_end }: { period_end: string | undefined }) {
    return (
      <div className="flex flex-col gap-y-2">
        <h4 className="font-bold">
          {t.MEMBERSHIP_SETTINGS.LABELS.FULL_MEMBER_TITLE}
        </h4>
        <div className="text-sm text-gray-400">
          {(
            t.MEMBERSHIP_SETTINGS.LABELS.FULL_MEMBER_DESCRIPTION as (
              date: string,
            ) => React.ReactNode
          )(formatSubscriptionDate(period_end, locale.code))}
        </div>
      </div>
    );
  }

  function FullMemberRecurring({ details }: { details: SubscriptionDetails }) {
    return (
      <div className="flex flex-col gap-y-2">
        <h4 className="font-bold">
          {t.MEMBERSHIP_SETTINGS.LABELS.FULL_MEMBER_TITLE}
        </h4>
        <div className="text-sm text-gray-400">
          {(
            t.MEMBERSHIP_SETTINGS.LABELS.FULL_MEMBER_RECURRING_DESCRIPTION as (
              date: string,
            ) => React.ReactNode
          )(formatSubscriptionDate(details.period_end, locale.code))}
        </div>
        <div className="flex flex-row items-center text-gray-400 text-sm gap-1">
          <CreditCardIcon className="w-5" />
          {formatCardType(details.card_type)}{" "}
          {t.MEMBERSHIP_SETTINGS.LABELS.ENDING_IN}
          <span className="font-mono">{details.last_four}</span>
        </div>
        <div>
          <CancelSubscriptionButton details={details} />
        </div>
      </div>
    );
  }

  function FullMemberRecurringAppleIAP({
    details,
  }: {
    details: SubscriptionDetails;
  }) {
    return (
      <div className="flex flex-col gap-y-2">
        <h4 className="font-bold">
          {t.MEMBERSHIP_SETTINGS.LABELS.FULL_MEMBER_TITLE}
        </h4>
        <div className="text-sm text-gray-400">
          {(
            t.MEMBERSHIP_SETTINGS.LABELS.FULL_MEMBER_RECURRING_DESCRIPTION as (
              date: string,
            ) => React.ReactNode
          )(formatSubscriptionDate(details.period_end, locale.code))}
        </div>
        <div>
          <Button
            asChild
            variant="link"
            className="p-0 underline text-zinc-400"
          >
            <a
              href="https://apps.apple.com/account/subscriptions"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.MEMBERSHIP_SETTINGS.LABELS.MANAGE_SUBSCRIPTION}
            </a>
          </Button>
        </div>
      </div>
    );
  }

  function FullMemberRecurringCancelled({
    period_end,
  }: {
    period_end: string | undefined;
  }) {
    return (
      <div className="flex flex-col gap-y-2">
        <h4 className="font-bold">
          {t.MEMBERSHIP_SETTINGS.LABELS.FULL_MEMBER_TITLE}
        </h4>
        <div className="text-sm text-gray-400">
          {(
            t.MEMBERSHIP_SETTINGS.LABELS.FULL_MEMBER_CANCELLED_DESCRIPTION as (
              date: string,
            ) => React.ReactNode
          )(formatSubscriptionDate(period_end, locale.code))}
        </div>
      </div>
    );
  }

  function PreviousMember({ period_end }: { period_end: string | undefined }) {
    return (
      <div className="flex flex-col gap-y-2">
        <h4 className="font-bold">
          {t.MEMBERSHIP_SETTINGS.LABELS.MEMBERSHIP_ENDED_TITLE}
        </h4>
        <div className="text-sm text-gray-400">
          {(
            t.MEMBERSHIP_SETTINGS.LABELS.MEMBERSHIP_ENDED_DESCRIPTION as (
              date: string,
            ) => React.ReactNode
          )(formatSubscriptionDate(period_end, locale.code))}
        </div>
      </div>
    );
  }

  function UpgradeButton({ label }: { label: string }) {
    return (
      <Button className="sm:w-1/2" onClick={() => navigate("/upgrade")}>
        <CreditCardIcon className="w-5 h-5 mr-2" />
        {label}
      </Button>
    );
  }

  const data = activeSubscriptionQuery.data;
  const subscriptionStatus = data?.subscription_status;

  return (
    <Card className="dark w-full max-w-lg">
      <CardHeader>
        <CardTitle className="text-xl">
          {t.MEMBERSHIP_SETTINGS.LABELS.YOUR_MEMBERSHIP_TITLE}
        </CardTitle>
      </CardHeader>
      <CardContent className="dark">
        {activeSubscriptionQuery.isLoading ? (
          <Spinner />
        ) : activeSubscriptionQuery.isError || !data ? (
          <div className="text-gray-500">
            {t.MEMBERSHIP_SETTINGS.ERRORS.MEMBERSHIP_LOADING}
          </div>
        ) : (
          <div className="flex flex-col gap-y-4">
            {subscriptionStatus === SubscriptionStatus.ACTIVE && (
              <>
                {data.details?.is_recurring ? (
                  <>
                    {data.details.payment_gateway ===
                    PaymentGatewayType.APPLE_IAP ? (
                      <FullMemberRecurringAppleIAP details={data.details} />
                    ) : (
                      <FullMemberRecurring details={data.details} />
                    )}
                  </>
                ) : (
                  <>
                    <FullMember period_end={data.details?.period_end} />
                    <UpgradeButton
                      label={t.MEMBERSHIP_SETTINGS.LABELS.EXTEND_BUTTON}
                    />
                  </>
                )}
              </>
            )}
            {subscriptionStatus === SubscriptionStatus.ACTIVE_CANCELED && (
              <FullMemberRecurringCancelled
                period_end={data.details?.period_end}
              />
            )}
            {subscriptionStatus === SubscriptionStatus.INACTIVE && (
              <>
                <FreePreview />
                <UpgradeButton
                  label={t.MEMBERSHIP_SETTINGS.LABELS.UPGRADE_BUTTON}
                />
              </>
            )}
            {subscriptionStatus === SubscriptionStatus.EXPIRED && (
              <>
                <PreviousMember period_end={data.details?.period_end} />
                <UpgradeButton
                  label={t.MEMBERSHIP_SETTINGS.LABELS.UPGRADE_BUTTON}
                />
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
